import { defineStore } from "pinia"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

// Recently visited detail pages, per entity type, in `localStorage`.
//
// Two consumers, with different needs:
// - the dashboard's "recent projects" card, through the [[recentProjects]] view over this store;
// - the command palette, which lists them on an empty query and — the reason this store is not
//   projects-only — pairs an entity with its tabs, so typing "files" can offer
//   "Files — Sabine Brandl" without the user first naming the entity.
//
// The type keys are the ones `services/globalSearch.js` uses (`SearchEntityType` on the backend), so
// a recent entry, a search hit and a tab catalogue all address a type the same way.

const STORAGE_KEY = "recent_entities"

/**
 * Where recently visited projects lived before this store generalised over every type. Nothing
 * writes it any more, but browsers that used an older build still hold it — and it holds one
 * tenant's project names, which must not survive a logout or a tenant switch. So it is cleared
 * alongside the current key rather than left behind for good.
 */
const LEGACY_PROJECTS_KEY = "recent_projects"

/** Per type, not overall — one busy afternoon in projects must not evict every customer. */
const MAX_PER_TYPE = 5

/**
 * The list inside the Erklärung's frame: the sample company's visits, kept in memory for the frame's
 * short life. The frame starts from a copy of the reader's `localStorage` (services/viewHelp/isolation.ts),
 * so reading it there would put the reader's real projects on a dashboard full of sample data. See
 * docs/view-help.md.
 */
let embedded = null

/**
 * The frame's starting list — the sample company's visits, handed over by `main.js` once it has loaded
 * them. Not imported here: this store is in every session's bundle, and the sample world is not.
 */
export function provideEmbeddedRecentEntities(entities) {
  embedded = entities
}

function read() {
  if (IS_VIEW_HELP_EMBED) return (embedded ??= {})
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    const parsed = stored ? JSON.parse(stored) : {}
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}
  } catch (error) {
    console.error("Failed to load recent entities", error)
    return {}
  }
}

function write(entities) {
  // The Erklärung's frame keeps its list in memory, beside the sample one it was given. A visit inside the
  // frame still moves the record up that list, as it would in the product.
  if (IS_VIEW_HELP_EMBED) {
    embedded = entities
    return
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entities))
  } catch (error) {
    console.error("Failed to save recent entity", error)
  }
}

export const useRecentEntitiesStore = defineStore("recentEntities", {
  getters: {
    /** Most recently visited first. Unknown type → empty, never undefined. */
    getRecentEntities: () => type => (read()[type] ?? []).slice(0, MAX_PER_TYPE),
  },
  actions: {
    /**
     * Records a visit. A repeat visit moves the entry to the front rather than duplicating it, and
     * refreshes the title — an entity renamed since the last visit would otherwise keep showing its
     * old name until it fell out of the list.
     */
    addRecentEntity({ type, id, title, subtitle = "" }) {
      if (!type || !id || !title) return

      const entities = read()
      const withoutThisOne = (entities[type] ?? []).filter(entity => entity.id !== id)

      entities[type] = [{ id, title, subtitle, visitedAt: new Date().toISOString() }, ...withoutThisOne].slice(
        0,
        MAX_PER_TYPE
      )
      write(entities)
    },
    /**
     * Drops one entry, for an entity that turned out to be gone or no longer the user's to open.
     * The list is written on a visit and never revisited, so nothing else would ever clear it.
     */
    removeRecentEntity({ type, id }) {
      if (!type || !id) return

      const entities = read()
      const remaining = (entities[type] ?? []).filter(entity => entity.id !== id)
      if (remaining.length === (entities[type] ?? []).length) return

      entities[type] = remaining
      write(entities)
    },
    /**
     * Drops everything. Called when the session identity changes (logout, 401, tenant switch) — the
     * list names entities of one tenant, so it must never survive into another.
     */
    clearRecentEntities() {
      // The frame's list is its own; the reader's, in the shared storage, is not the frame's to clear.
      if (IS_VIEW_HELP_EMBED) {
        embedded = {}
        return
      }
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(LEGACY_PROJECTS_KEY)
    },
  },
})
